/** Retry an authenticated, account-bound request after access-cookie expiry. */
const refreshes = new Map<string, Promise<boolean>>();
function refreshSession(base: string) {
  const pending = refreshes.get(base);
  if (pending) return pending;
  const request = fetch(base + '/api/auth/refresh', { method: 'POST', credentials: 'include', cache: 'no-store' })
    .then(response => response.ok).finally(() => { refreshes.delete(base); });
  refreshes.set(base, request);
  return request;
}

export async function sessionFetch(base: string, url: string | URL, options: RequestInit,
  userId: string, current: () => boolean): Promise<Response> {
  const check = () => { if (!current()) throw new Error('The selected account changed. Return to the original account.'); };
  const send = () => fetch(url, { ...options, credentials: 'include', cache: 'no-store' });
  check();
  let response = await send();
  check();
  if (response.status !== 401) return response;
  // These callers use expected_user_id and saved request IDs. A 401 is rejected
  // before their handler runs, so the exact request can safely be retried once.
  if (!await refreshSession(base)) throw new Error('Your session expired. Sign in again, then recover your saved change.');
  check();
  const identity = await fetch(base + '/api/auth/me', { credentials: 'include', cache: 'no-store' });
  const data = await identity.json().catch(() => null);
  check();
  if (!identity.ok || data?.success !== true || String(data.user?.id) !== userId) {
    throw new Error('The signed-in account changed. Sign in with the original account before retrying.');
  }
  response = await send();
  check();
  if (response.status === 401) throw new Error('Your session expired. Sign in again, then recover your saved change.');
  return response;
}
